import React, { useEffect, useState } from 'react';
import { Activity, LogIn, LogOut, KeyRound, Eye, Shield, AlertCircle } from 'lucide-react';
import { saSystemActivity } from '../../lib/db';
import { useRoute } from '../../lib/router';

const ACTION_META = {
  login:                   { icon: LogIn,    label: 'Sign-in',                  tone: 'emerald' },
  logout:                  { icon: LogOut,   label: 'Sign-out',                 tone: 'slate'   },
  password_change:         { icon: KeyRound, label: 'Password changed',         tone: 'amber'   },
  password_setup:          { icon: KeyRound, label: 'Password set',             tone: 'amber'   },
  password_reset_sent:     { icon: KeyRound, label: 'Password reset sent',      tone: 'amber'   },
  session_resume:          { icon: LogIn,    label: 'Session resumed',          tone: 'slate'   },
  impersonation_start:     { icon: Eye,      label: 'Impersonation started',    tone: 'red'     },
  impersonation_end:       { icon: Eye,      label: 'Impersonation ended',      tone: 'slate'   },
  start_impersonation:     { icon: Eye,      label: 'Impersonation started',    tone: 'red'     },
  end_impersonation:       { icon: Eye,      label: 'Impersonation ended',      tone: 'slate'   },
  extend_trial:            { icon: Shield,   label: 'Trial extended',           tone: 'amber'   },
};

const TONE_CLASSES = {
  emerald: 'bg-emerald-900/40 text-emerald-300',
  amber:   'bg-amber-900/40 text-amber-300',
  red:     'bg-red-900/40 text-red-300',
  slate:   'bg-slate-700 text-slate-300',
  indigo:  'bg-indigo-900/40 text-indigo-300',
};

const ALL_FILTERS = [
  { key: 'all',           label: 'All events' },
  { key: 'auth',          label: 'Sign-ins / outs' },
  { key: 'password',      label: 'Password events' },
  { key: 'impersonation', label: 'Impersonation' },
  { key: 'super_admin',   label: 'Super-admin ops' },
];

function actionMeta(action) {
  // Catch prefixed actions like "set_plan:basic->pro", "promote_super_admin:<uuid>"
  if (action?.startsWith('set_plan')) return { icon: Shield, label: 'Plan changed', tone: 'indigo' };
  if (action?.startsWith('promote_super_admin')) return { icon: Shield, label: 'Super-admin promoted', tone: 'indigo' };
  if (action?.startsWith('demote_super_admin'))  return { icon: Shield, label: 'Super-admin demoted',  tone: 'red' };
  if (action?.startsWith('password_reset_sent')) return ACTION_META.password_reset_sent;
  return ACTION_META[action] || { icon: Activity, label: action || 'event', tone: 'slate' };
}

function filterMatch(filter, action, source) {
  if (filter === 'all') return true;
  if (filter === 'auth') return ['login','logout','session_resume'].includes(action);
  if (filter === 'password') return action?.startsWith('password');
  if (filter === 'impersonation') return action?.includes('impersonation');
  if (filter === 'super_admin') return source === 'super_admin_audit' || action?.startsWith('set_plan') || action?.startsWith('promote_super_admin') || action?.startsWith('demote_super_admin') || action === 'extend_trial';
  return true;
}

function formatTimestamp(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

const PAGE_SIZE = 100;

export default function SuperAdminActivityPage() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error,   setError]   = useState('');
  const [filter,  setFilter]  = useState('all');
  const [hasMore, setHasMore] = useState(true);
  const [, navigate] = useRoute();

  const load = (offset = 0, append = false) => {
    if (append) setLoadingMore(true); else setLoading(true);
    saSystemActivity(PAGE_SIZE, offset).then(({ data, error: err }) => {
      if (err) {
        setError(err.message || 'Could not load activity');
      } else {
        const next = data || [];
        setRows((prev) => append ? [...prev, ...next] : next);
        setHasMore(next.length === PAGE_SIZE);
      }
      setLoading(false);
      setLoadingMore(false);
    });
  };
  useEffect(() => { load(0, false); }, []);

  const visible = rows.filter((r) => filterMatch(filter, r.action, r.source));

  if (loading) return <div className="text-center py-20 text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-white">System Activity</h1>
        <p className="text-slate-400 text-sm mt-0.5">
          Authentication and admin events across all firms. Newest first.
        </p>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-xl bg-red-900/30 border border-red-800 text-red-300 text-sm flex items-start gap-2">
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 text-xs">
        {ALL_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-2.5 py-1 rounded-lg transition ${
              filter === f.key
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-800/40">
            <tr>
              <th className="text-left px-5 py-3 font-medium whitespace-nowrap">When</th>
              <th className="text-left px-5 py-3 font-medium">Event</th>
              <th className="text-left px-5 py-3 font-medium">Firm</th>
              <th className="text-left px-5 py-3 font-medium">User</th>
              <th className="text-left px-5 py-3 font-medium">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {visible.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">No events match.</td></tr>
            )}
            {visible.map((r) => {
              const meta = actionMeta(r.action);
              const Icon = meta.icon;
              return (
                <tr key={r.id} className="hover:bg-slate-800/30 transition">
                  <td className="px-5 py-3 text-xs text-slate-400 whitespace-nowrap">{formatTimestamp(r.created_at)}</td>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-1.5">
                      <Icon size={13} className="text-slate-500" />
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TONE_CLASSES[meta.tone] || TONE_CLASSES.slate}`}>
                        {meta.label}
                      </span>
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    {r.firm_id ? (
                      <button
                        onClick={() => navigate(`/superadmin/firms/${r.firm_id}`)}
                        className="text-slate-300 hover:text-indigo-300 underline-offset-2 hover:underline transition text-sm"
                      >
                        {r.firm_name || r.firm_id.slice(0, 8)}
                      </button>
                    ) : (
                      <span className="text-slate-600 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-slate-400 text-sm">{r.user_email || '—'}</td>
                  <td className="px-5 py-3 text-xs font-mono text-slate-500">{r.ip_address || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="px-5 py-3 border-t border-slate-800 flex items-center justify-between text-xs">
          <span className="text-slate-500">
            Showing {visible.length} of {rows.length} loaded events
          </span>
          {hasMore && (
            <button
              onClick={() => load(rows.length, true)}
              disabled={loadingMore}
              className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 disabled:opacity-40"
            >
              {loadingMore ? 'Loading…' : 'Load older'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
